import dayjs from '../../utils/dayjs';
import React from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { salesAPI } from '../../api';
import { useCurrency } from '../../contexts/CurrencyContext';
import { Search, X, Eye, Edit } from 'lucide-react';
import DataTable from '../ui/DataTable';
import { readParam, useSyncUrlParams } from '../../hooks/useUrlListState';

// Results-banner wording for the applied total range ('' = no limit on that side).
const describeTotalRange = (min, max) => {
  const rs = (value) => `Rs. ${Number(value).toLocaleString()}`;
  if (min && max) return `with total ${rs(min)} to ${rs(max)}`;
  if (min) return `with total ${rs(min)} or more`;
  if (max) return `with total up to ${rs(max)}`;
  return '';
};

const SalesList = () => {
  const navigate = useNavigate();
  const { formatPrice } = useCurrency();

  // Column definitions for DataTable
  const columns = [
    {
      key: 'ID',
      title: 'Sale #',
      sortable: true,
      render: (value, row) => (
        <div>
          <div className="text-sm font-medium text-gray-900">{row.SaleNo || value}</div>
          {value && <div className="text-xs text-gray-500">ID: {value}</div>}
        </div>
      )
    },
    {
      key: 'SaleDate',
      title: 'Date',
      sortable: true,
      render: (value) => {
        if (!value) return <div className="text-sm text-gray-900">-</div>;
        const date = dayjs(value).local();
        // Two lines that never wrap, like the Sale # cell: the date over the time.
        // One line ("03 Nov 2026, 05:18 PM") pushes the table off a laptop screen.
        return (
          <div className="whitespace-nowrap text-sm">
            <div className="text-gray-900">{date.format('DD MMM YYYY')}</div>
            <div className="text-gray-500">{date.format('hh:mm A')}</div>
          </div>
        );
      }
    },
    {
      key: 'CustomerName',
      title: 'Customer',
      sortable: true,
      render: (value, row) => (
        <div>
          <div className="text-sm text-gray-900">{value}</div>
        </div>
      )
    },
    {
      // Pieces sold: the sum of the sale's line quantities.
      key: 'itemsQty',
      title: 'Items',
      sortable: true,
      render: (value) => (
        <div className="text-sm text-gray-900">{Number(value) || 0}</div>
      )
    },
    {
      key: 'totalAmount',
      title: 'Total Amount',
      sortable: true,
      render: (value, row) => (
        <div>
          <div className="whitespace-nowrap text-sm text-gray-900">Rs. {value}</div>
        </div>
      )
    },
    {
      key: 'totalDiscount',
      title: 'Total Discount',
      sortable: true,
      render: (value, row) => (
        <div>
          <div className="whitespace-nowrap text-sm text-gray-900">Rs. {value}</div>
        </div>
      )
    },
    {
      key: 'additionalDiscount',
      title: 'Add. Discount',
      sortable: true,
      render: (value, row) => (
        <div>
          <div className="whitespace-nowrap text-sm text-gray-900">Rs. {value}</div>
        </div>
      )
    },
    {
      key: 'randomKey',
      title: 'Total',
      sortable: true,
      render: (value, row) => (
        <div>
          <div className="whitespace-nowrap text-sm text-gray-900">Rs. {row.totalAmount - row.totalDiscount - row.additionalDiscount}</div>
        </div>
      )
    },
    {
      key: 'BankReceived',
      title: 'Bank Received',
      sortable: true,
      render: (value, row) => (
        <div>
          <div className="whitespace-nowrap text-sm text-gray-900">Rs. {row.BankReceived}</div>
        </div>
      )
    },
    {
      key: 'CashReceived',
      title: 'Cash Received',
      sortable: true,
      render: (value, row) => (
        <div>
          <div className="whitespace-nowrap text-sm text-gray-900">Rs. {row.CashReceived}</div>
        </div>
      )
    },
    {
      key: 'Remarks',
      title: 'Remarks',
      sortable: false,
      render: (value, row) => (
        <div className="text-sm text-gray-700 truncate max-w-xs">{row.SaleRemarks || value || '-'}</div>
      )
    },
    {
      key: 'actions',
      title: 'Actions',
      sortable: false,
      render: (value, row) => (
        <div className="flex items-center gap-2">
          <button
            onClick={() => navigate(`/sales/${row.ID}`)}
            className="p-2 text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded-lg transition-colors"
            title="View"
          >
            <Eye className="h-4 w-4" />
          </button>
        </div>
      )
    }
  ];

  // Search/filter/sort/pagination state is seeded from the URL so the view is
  // shareable and restored when returning from a sale's detail page.
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = React.useState(true);
  const [sales, setSales] = React.useState([]);
  const [searchTerm, setSearchTerm] = React.useState(() => readParam(searchParams, 'q', ''));
  // Item name or barcode: only sales that contain a matching item.
  const [itemTerm, setItemTerm] = React.useState(() => readParam(searchParams, 'item', ''));
  // Total range in Rs (the "Total" column, after all discounts); '' = no limit.
  const [minTotal, setMinTotal] = React.useState(() => readParam(searchParams, 'min_total', ''));
  const [maxTotal, setMaxTotal] = React.useState(() => readParam(searchParams, 'max_total', ''));
  const [isSearching, setIsSearching] = React.useState(
    () => ['q', 'item', 'min_total', 'max_total'].some((key) => readParam(searchParams, key, ''))
  );
  const [sortBy, setSortBy] = React.useState(() => readParam(searchParams, 'sort', 'ID'));
  const [sortOrder, setSortOrder] = React.useState(() => readParam(searchParams, 'order', 'desc'));
  const [startDate, setStartDate] = React.useState(() => readParam(searchParams, 'start', ''));
  const [endDate, setEndDate] = React.useState(() => readParam(searchParams, 'end', ''));

  // Draft inputs: what's typed into the search box / date fields BEFORE the user
  // hits Search. Committed to the applied query above only by handleSearch.
  const [draftSearch, setDraftSearch] = React.useState(() => readParam(searchParams, 'q', ''));
  const [draftItem, setDraftItem] = React.useState(() => readParam(searchParams, 'item', ''));
  const [draftMinTotal, setDraftMinTotal] = React.useState(() => readParam(searchParams, 'min_total', ''));
  const [draftMaxTotal, setDraftMaxTotal] = React.useState(() => readParam(searchParams, 'max_total', ''));
  const [draftStartDate, setDraftStartDate] = React.useState(() => readParam(searchParams, 'start', ''));
  const [draftEndDate, setDraftEndDate] = React.useState(() => readParam(searchParams, 'end', ''));

  // Pagination
  const [currentPage, setCurrentPage] = React.useState(() => readParam(searchParams, 'page', 1, 'number'));
  const [totalPages, setTotalPages] = React.useState(1);
  const [itemsPerPage, setItemsPerPage] = React.useState(() => readParam(searchParams, 'size', 20, 'number'));

  // Mirror the current view into the URL (defaults omitted).
  useSyncUrlParams(
    {
      q: searchTerm.trim(), item: itemTerm.trim(), min_total: minTotal, max_total: maxTotal,
      sort: sortBy, order: sortOrder, start: startDate, end: endDate, page: currentPage, size: itemsPerPage,
    },
    { q: '', item: '', min_total: '', max_total: '', sort: 'ID', order: 'desc', start: '', end: '', page: 1, size: 20 },
  );
  const searchInputRef = React.useRef(null);
  // Skip the first run of the applied-change / sort effects so state restored
  // from the URL isn't re-loaded or reset to page 1 on mount.
  const appliedInitRef = React.useRef(false);
  const sortInitRef = React.useRef(false);

  const loadData = React.useCallback(async (page = 1, limit = 20, search = '') => {
    try {
      setLoading(true);
      const skip = (page - 1) * limit;
      const params = { skip, limit, sort: sortBy, order: sortOrder };
      if (search && search.trim()) {
        params.q = search.trim();
      }
      if (itemTerm.trim()) {
        params.item = itemTerm.trim();
      }
      if (minTotal) {
        params.min_total = minTotal;
      }
      if (maxTotal) {
        params.max_total = maxTotal;
      }
      if (startDate) {
        params.start_date = new Date(startDate).toISOString();
      }
      if (endDate) {
        const end = new Date(endDate);
        if (endDate.length <= 10) {
          end.setHours(23, 59, 59, 999);
        }
        params.end_date = end.toISOString();
      }
      
      console.log('Loading sales with params:', params);
      const res = await salesAPI.list(params);
      const list = res.data || [];

      // Sorting is done server-side (params.sort / params.order).
      setSales(list);
      // naive next-page check
      if ((res.data || []).length === limit) {
        setTotalPages(page + 1);
      } else {
        setTotalPages(page);
      }
    } catch (err) {
      console.error('Failed to load sales', err);
      setSales([]);
    } finally {
      setLoading(false);
    }
  }, [sortBy, sortOrder, itemTerm, minTotal, maxTotal, startDate, endDate]);

  // Reload when the APPLIED search/item/total/date filters change (Search / Clear),
  // not as you type. Skips the first run so the mount load below isn't duplicated.
  React.useEffect(() => {
    if (!appliedInitRef.current) {
      appliedInitRef.current = true;
      return;
    }
    setCurrentPage(1);
    setIsSearching(Boolean(searchTerm.trim() || itemTerm.trim() || minTotal || maxTotal));
    loadData(1, itemsPerPage, searchTerm);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm, itemTerm, minTotal, maxTotal, startDate, endDate]);

  // Re-query (server-side sort) when the sort changes; keep the current page.
  React.useEffect(() => {
    if (!sortInitRef.current) {
      sortInitRef.current = true;
      return;
    }
    loadData(currentPage, itemsPerPage, searchTerm);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sortBy, sortOrder]);

  React.useEffect(() => {
    // Initial load respects the page/search restored from the URL.
    loadData(currentPage, itemsPerPage, searchTerm);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // initial

  React.useEffect(() => {
    // Focus search input after component mounts
    if (searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, []);

  const handleSort = (field) => {
    const newOrder = sortBy === field && sortOrder === 'asc' ? 'desc' : 'asc';
    setSortBy(field);
    setSortOrder(newOrder);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
    loadData(page, itemsPerPage, searchTerm);
  };

  const handleItemsPerPageChange = (e) => {
    const limit = parseInt(e.target.value);
    setItemsPerPage(limit);
    setCurrentPage(1);
    loadData(1, limit, searchTerm);
  };

  // Apply the typed search + item + total + date filters (Search button / Enter). The
  // applied-change effect does the fetch and page reset — nothing loads as you type.
  const handleSearch = () => {
    setSearchTerm(draftSearch);
    setItemTerm(draftItem);
    setMinTotal(draftMinTotal);
    setMaxTotal(draftMaxTotal);
    setStartDate(draftStartDate);
    setEndDate(draftEndDate);
  };

  const searchOnEnter = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSearch();
    }
  };

  const clearSearch = () => {
    setDraftSearch('');
    setSearchTerm('');
  };

  // Everything the results banner describes: the main search, item and total range.
  const clearSearchFilters = () => {
    clearSearch();
    setDraftItem('');
    setItemTerm('');
    setDraftMinTotal('');
    setDraftMaxTotal('');
    setMinTotal('');
    setMaxTotal('');
  };

  const clearAll = () => {
    clearSearchFilters();
    setDraftStartDate('');
    setDraftEndDate('');
    setStartDate('');
    setEndDate('');
  };

  const searchSummary = [
    searchTerm.trim() && `for "${searchTerm.trim()}"`,
    itemTerm.trim() && `with item "${itemTerm.trim()}"`,
    describeTotalRange(minTotal, maxTotal),
  ].filter(Boolean).join(' ');

  if (loading) {
    return (
      <div className="flex justify-center items-center p-8">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Sales</h1>
        <p className="text-gray-600">Browse sales and open details to edit.</p>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
        {/* Main Search Bar */}
        <div className="flex gap-2 mb-4">
          <div className="relative flex-1">
            <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400 pointer-events-none" />
            <input
              ref={searchInputRef}
              type="text"
              placeholder="Search by Sale No, Customer, or Phone..."
              value={draftSearch}
              onChange={(e) => setDraftSearch(e.target.value)}
              onKeyDown={searchOnEnter}
              style={{ fontSize: '1rem' }}
              className="w-full h-12 pl-11 pr-10 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
            {draftSearch && (
              <button
                onClick={clearSearch}
                className="absolute right-3.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                <X className="h-5 w-5" />
              </button>
            )}
          </div>
          <button
            onClick={handleSearch}
            className="h-12 px-6 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-colors flex items-center justify-center gap-2 font-medium whitespace-nowrap"
          >
            <Search className="h-5 w-5" />
            Search
          </button>
        </div>

        {/* Advanced Filters */}
        <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold text-gray-900">Advanced filters</h3>
            <button
              onClick={clearAll}
              className="flex items-center gap-1 text-sm font-medium text-gray-500 hover:text-gray-700 transition-colors"
            >
              <X className="h-3.5 w-3.5" />
              Clear all
            </button>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div>
              <label htmlFor="sales-item-filter" className="block text-sm font-medium text-gray-700 mb-1">Item</label>
              <input
                id="sales-item-filter"
                type="text"
                placeholder="Item name or barcode"
                value={draftItem}
                onChange={(e) => setDraftItem(e.target.value)}
                onKeyDown={searchOnEnter}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Start date</label>
              <input
                type="date"
                value={draftStartDate}
                onChange={(e) => setDraftStartDate(e.target.value)}
                onKeyDown={searchOnEnter}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">End date</label>
              <input
                type="date"
                value={draftEndDate}
                onChange={(e) => setDraftEndDate(e.target.value)}
                onKeyDown={searchOnEnter}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <div>
              <label htmlFor="sales-min-total" className="block text-sm font-medium text-gray-700 mb-1">Total from (Rs)</label>
              <input
                id="sales-min-total"
                type="number"
                min="0"
                inputMode="numeric"
                placeholder="Any"
                value={draftMinTotal}
                onChange={(e) => setDraftMinTotal(e.target.value)}
                onKeyDown={searchOnEnter}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <div>
              <label htmlFor="sales-max-total" className="block text-sm font-medium text-gray-700 mb-1">Total to (Rs)</label>
              <input
                id="sales-max-total"
                type="number"
                min="0"
                inputMode="numeric"
                placeholder="Any"
                value={draftMaxTotal}
                onChange={(e) => setDraftMaxTotal(e.target.value)}
                onKeyDown={searchOnEnter}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>
        </div>
      </div>

      {isSearching && (
        <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <div className="flex items-center justify-between">
            <p className="text-blue-800">Showing {sales.length} search results {searchSummary}</p>
            <button onClick={clearSearchFilters} className="text-blue-600 hover:text-blue-800 text-sm font-medium">Clear Search</button>
          </div>
        </div>
      )}

      <DataTable
        columns={columns}
        data={sales}
        loading={loading}
        currentPage={currentPage}
        totalPages={totalPages}
        itemsPerPage={itemsPerPage}
        totalItems={sales.length}
        onPageChange={handlePageChange}
        onItemsPerPageChange={handleItemsPerPageChange}
        sortBy={sortBy}
        sortOrder={sortOrder}
        onSort={handleSort}
        itemLabel="sales"
        onRowClick={(row, e) =>
          e.metaKey || e.ctrlKey
            ? window.open(`/sales/${row.ID}`, '_blank', 'noopener')
            : navigate(`/sales/${row.ID}`)
        }
      />
    </div>
  );
};

export default SalesList;






